import { ObjectTools } from '../../tools/object-tools';
import { SolContent } from './sol-content';
import {
  SolContentMarkdownOptions,
  SolContentMarkdownTools,
  SolMarkdownFeatures,
} from '../../tools/sol-content-markdown-tools';
import { NavigationTarget } from '../../tools/router-tools';

export class SolMarkdown {
  private constructor(
    public readonly text: string,
    public readonly assetBasePath: string | undefined,
    public readonly features: Partial<SolMarkdownFeatures> | undefined,
    public readonly linkTarget: NavigationTarget,
    public readonly imageResolver: SolContentMarkdownOptions['imageResolver'],
    public readonly content: SolContent,
    public readonly html: string,
  ) {}

  public static of(): SolMarkdown {
    return new SolMarkdown('', undefined, undefined, NavigationTarget.SameTab, undefined, SolContent.empty(), '');
  }

  public withText(text: string): SolMarkdown {
    if (this.text === text) {
      return this;
    }
    return this.copy({ text }).calculateContent();
  }

  public withAssetBasePath(assetBasePath: string | undefined): SolMarkdown {
    if (this.assetBasePath === assetBasePath) {
      return this;
    }
    return this.copy({ assetBasePath }).calculateContent();
  }

  public withFeatures(features: Partial<SolMarkdownFeatures> | undefined): SolMarkdown {
    if (ObjectTools.equals(this.features, features)) {
      return this;
    }
    return this.copy({ features }).calculateContent();
  }

  /** Builds each markdown image with the resolver instead of the default `SolImage`; return `undefined` to keep the default. */
  public withImageResolver(imageResolver: SolContentMarkdownOptions['imageResolver']): SolMarkdown {
    if (this.imageResolver === imageResolver) {
      return this;
    }
    return this.copy({ imageResolver }).calculateContent();
  }

  public withLinkTarget(linkTarget: NavigationTarget): SolMarkdown {
    if (this.linkTarget === linkTarget) {
      return this;
    }
    return this.copy({ linkTarget });
  }

  private calculateContent(): SolMarkdown {
    return this.copy({
      content: SolContentMarkdownTools.parse(this.text, {
        assetBasePath: this.assetBasePath,
        features: this.features,
        imageResolver: this.imageResolver,
      }),
      html: '',
    });
  }

  private copy(props: Partial<SolMarkdown>): SolMarkdown {
    return ObjectTools.copy<SolMarkdown>(this, props);
  }
}
